<template>
    <div class="Toggle">
        <input
            type="checkbox"

            :class="$attrs.class"

            :true-value="trueValue"
            :false-value="falseValue"

            :disabled="disabled"

            v-model="value"
        >
        <span class="slider"></span>
    </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps(
{
    trueValue  : { type: [ String, Number, Boolean ], default: true },
    falseValue : { type: [ String, Number, Boolean ], default: false },
    modelValue : { type: [ String, Number, Boolean ], default: false },
    disabled   : { type: Boolean, default: false },
})

const emit = defineEmits([ "update:modelValue" ] );

const value = computed(
{
    get()
    {
        return props.modelValue;
    },
    set( value )
    {
        emit( 'update:modelValue', value )
    }
})
</script>

<style lang="scss">
body
{
    &[data-theme="light"]
    {
        .Toggle
        {
            --toggle-background-color: var(--light-surfaces-gainsboro);
            --toggle-hover-background-color: var(--light-surfaces-silver);
            --toggle-focus-background-color: var(--light-surfaces-silver);
            --toggle-disabled-background-color: var(--light-surfaces-gainsboro);

            --toggle-active-background-color: var(--primary-colors-blue-60);
            --toggle-active-hover-background-color: var(--primary-colors-blue-60);
            --toggle-active-focus-background-color: var(--primary-colors-blue-60);
            --toggle-active-disabled-background-color: var(--primary-colors-blue-60);

            --toggle-slider-background-color: var(--light-surfaces-white);
            --toggle-hover-slider-background-color: var(--light-surfaces-white);
            --toggle-focus-slider-background-color: var(--light-surfaces-white);
            --toggle-disabled-slider-background-color: var(--light-surfaces-white);

            --toggle-slider-border-color: var(--toggle-background-color);
            --toggle-hover-slider-border-color: var(--toggle-hover-background-color);
            --toggle-focus-slider-border-color: var(--toggle-focus-background-color);
            --toggle-disabled-slider-border-color: var(--toggle-disabled-background-color);

            --toggle-active-slider-background-color: var(--light-surfaces-white);
            --toggle-active-hover-slider-background-color: var(--light-surfaces-white);
            --toggle-active-focus-slider-background-color: var(--light-surfaces-white);
            --toggle-active-disabled-slider-background-color: var(--light-surfaces-white);

            --toggle-active-slider-border-color: var(--toggle-active-background-color);
            --toggle-active-hover-slider-border-color: var(--toggle-active-hover-background-color);
            --toggle-active-focus-slider-border-color: var(--toggle-active-focus-background-color);
            --toggle-active-disabled-slider-border-color: var(--toggle-active-disabled-background-color);

            --toggle-slider-shadow-color: rgba( 0, 0, 0, 0.25 );
            --toggle-hover-slider-shadow-color: rgba( 0, 0, 0, 0.25 );
            --toggle-focus-slider-shadow-color: rgba( 0, 0, 0, 0.25 );
            --toggle-disabled-slider-shadow-color: rgba( 0, 0, 0, 0.25 );

            --toggle-active-slider-shadow-color: rgba( 0, 0, 0, 0.25 );
            --toggle-active-hover-slider-shadow-color: rgba( 0, 0, 0, 0.25 );
            --toggle-active-focus-slider-shadow-color: rgba( 0, 0, 0, 0.25 );
            --toggle-active-disabled-slider-shadow-color: rgba( 0, 0, 0, 0.25 );
        }
    }

    &[data-theme="dark"]
    {
        .Toggle
        {
            --toggle-background-color: var(--dark-surfaces-slate);
            --toggle-hover-background-color: var(--dark-surfaces-slate);
            --toggle-focus-background-color: var(--dark-surfaces-slate);
            --toggle-disabled-background-color: var(--dark-surfaces-slate);

            --toggle-active-background-color: var(--primary-colors-blue-40);
            --toggle-active-hover-background-color: var(--primary-colors-blue-40);
            --toggle-active-focus-background-color: var(--primary-colors-blue-40);
            --toggle-active-disabled-background-color: var(--primary-colors-blue-40);

            --toggle-slider-background-color: var(--light-surfaces-white);
            --toggle-hover-slider-background-color: var(--light-surfaces-white);
            --toggle-focus-slider-background-color: var(--light-surfaces-white);
            --toggle-disabled-slider-background-color: var(--light-surfaces-white);

            --toggle-slider-border-color: var(--toggle-background-color);
            --toggle-hover-slider-border-color: var(--toggle-hover-background-color);
            --toggle-focus-slider-border-color: var(--toggle-focus-background-color);
            --toggle-disabled-slider-border-color: var(--toggle-disabled-background-color);

            --toggle-active-slider-background-color: var(--light-surfaces-white);
            --toggle-active-hover-slider-background-color: var(--light-surfaces-white);
            --toggle-active-focus-slider-background-color: var(--light-surfaces-white);
            --toggle-active-disabled-slider-background-color: var(--light-surfaces-white);

            --toggle-active-slider-border-color: var(--toggle-active-background-color);
            --toggle-active-hover-slider-border-color: var(--toggle-active-hover-background-color);
            --toggle-active-focus-slider-border-color: var(--toggle-active-focus-background-color);
            --toggle-active-disabled-slider-border-color: var(--toggle-active-disabled-background-color);

            --toggle-slider-shadow-color: rgba( 0, 0, 0, 0.25 );
            --toggle-hover-slider-shadow-color: rgba( 0, 0, 0, 0.25 );
            --toggle-focus-slider-shadow-color: rgba( 0, 0, 0, 0.25 );
            --toggle-disabled-slider-shadow-color: rgba( 0, 0, 0, 0.25 );

            --toggle-active-slider-shadow-color: rgba( 0, 0, 0, 0.25 );
            --toggle-active-hover-slider-shadow-color: rgba( 0, 0, 0, 0.25 );
            --toggle-active-focus-slider-shadow-color: rgba( 0, 0, 0, 0.25 );
            --toggle-active-disabled-slider-shadow-color: rgba( 0, 0, 0, 0.25 );
        }
    }
}

.Toggle
{
    position: relative;

    display: inline-block;

    width: var(--toggle-width);
    height: var(--toggle-height);

    &.sm
    {
        --toggle-width: #{px(24)};
        --toggle-height: #{px(16)};
        --toggle-padding: #{px(2)};
    }

    &.md
    {
        --toggle-width: #{px(32)};
        --toggle-height: #{px(20)};
        --toggle-padding: #{px(2)};
    }

    &.lg
    {
        --toggle-width: #{px(56)};
        --toggle-height: #{px(32)};
        --toggle-padding: #{px(4)};
    }

    input
    {
        position: absolute;
        top: 0!important;
        left: 0!important;
        width: 100%!important;
        height: 100%!important;
        opacity: 0!important;
    }

    .slider
    {
        position: absolute;
        cursor: pointer;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;

        border: 1px solid var(--toggle-slider-border-color);
        border-radius: var(--toggle-height);
        background-color: var(--toggle-background-color);

        transition: .4s;

        &:before
        {
            content: "";

            position: absolute;
            left: calc( var( --toggle-padding ) - 1px );
            top: calc( var(--toggle-padding ) - 1px );

            height: calc( var(--toggle-height) - var(--toggle-padding) * 2);
            width: calc( var(--toggle-height) - var(--toggle-padding) * 2);

            background-color: var(--toggle-slider-background-color);
            border-radius: 50%;

            box-shadow: 0 1px 2px var(--toggle-slider-shadow-color);

            transition: .4s;
        }
    }

    &:not(.active),
    :has(input:not(:checked))
    {
        &.hovered,
        &:hover
        {
            .slider
            {
                border-color: var(--toggle-hover-slider-border-color);
                background-color: var(--toggle-hover-background-color);

                &:before
                {
                    background-color: var(--toggle-hover-slider-background-color);
                    box-shadow: 0 1px 2px var(--toggle-hover-slider-shadow-color);
                }
            }
        }

        &.focused,
        &:focus
        {
            .slider
            {
                border: 1px dashed var(--toggle-focus-slider-border-color);
                background-color: var(--toggle-focus-background-color);

                &:before
                {
                    background-color: var(--toggle-focus-slider-background-color);
                    box-shadow: 0 0 1px var(--toggle-focus-slider-shadow-color);
                }
            }
        }

        &.disabled,
        &:has( input[ disabled ] )
        {
            pointer-events: none;
            opacity: 0.5;

            .slider
            {
                border-color: var(--toggle-disabled-slider-border-color);
                background-color: var(--toggle-disabled-background-color);

                &:before
                {
                    background-color: var(--toggle-disabled-slider-background-color);
                    box-shadow: 0 1px 2px var(--toggle-disabled-slider-shadow-color);
                }
            }
        }
    }

    &.active,
    &:has(input:checked)
    {
        .slider
        {
            border-color: var(--toggle-active-slider-border-color);
            background-color: var(--toggle-active-background-color);

            &:before
            {
                left: calc( 100% - var(--toggle-height) + var(--toggle-padding) + 1px );

                background-color: var(--toggle-active-slider-background-color);
                box-shadow: 0 1px 2px var(--toggle-active-slider-shadow-color);
            }
        }

        &.hovered,
        &:hover
        {
            .slider
            {
                border-color: var(--toggle-active-hover-slider-border-color);
                background-color: var(--toggle-active-hover-background-color);

                &:before
                {
                    background-color: var(--toggle-active-hover-slider-background-color);
                    box-shadow: 0 1px 2px var(--toggle-active-hover-slider-shadow-color);
                }
            }
        }

        &.focused,
        &:focus
        {
            .slider
            {
                border: 1px dashed var(--toggle-active-focus-slider-border-color);
                background-color: var(--toggle-active-focus-background-color);

                &:before
                {
                    background-color: var(--toggle-active-focus-slider-background-color);
                    box-shadow: 0 0 1px var(--toggle-active-focus-slider-shadow-color);
                }
            }
        }

        &.disabled,
        &:has( input[ disabled ] )
        {
            pointer-events: none;
            opacity: 0.5;

            .slider
            {
                border-color: var(--toggle-active-disabled-slider-border-color);
                background-color: var(--toggle-active-disabled-background-color);

                &:before
                {
                    background-color: var(--toggle-active-disabled-slider-background-color);
                    box-shadow: 0 1px 2px var(--toggle-active-disabled-slider-shadow-color);
                }
            }
        }
    }
}
</style>